import React, { useState } from 'react';
import { Card, Avatar, Typography, Box, Button, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { decrypt } from '../../../Security';

const VV = { primary: '#3F2B96', secondary: '#7F5AF0', accent: '#00C896', border: 'rgba(63,43,150,0.10)' };

const SingleUser = ({ user, setUserId }) => {
  const [blocked, setBlocked] = useState(user.blocked);
  const [currentRole, setCurrentRole] = useState(user.role);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(user.role);
  const [updating, setUpdating] = useState(false);

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return { bg: '#FFEBEE', color: '#C62828' };
      case 'representative': return { bg: '#FFF8E1', color: '#F57F17' };
      case 'user': return { bg: '#E3F2FD', color: '#1565C0' };
      default: return { bg: '#F5F5F5', color: '#616161' };
    }
  };

  const editUser = () => setUserId(user._id);

  const blockUser = async () => {
    if (!window.confirm(`${blocked ? 'Unblock' : 'Block'} user "${user.username}"?`)) return;
    try {
      const response = await fetch(process.env.REACT_APP_PUBLIC_SERVER_URI + '/adminWeb/blockUser/' + user._id, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = decrypt(await response.json());
      if (data.msg) {
        setBlocked(!blocked);
        alert(data.msg);
      } else alert(JSON.stringify(data.error));
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const updateUserRole = async () => {
    if (selectedRole === currentRole) {
      setRoleDialogOpen(false);
      return;
    }
    try {
      setUpdating(true);
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_SERVER_URI}/adminWeb/users/${user._id}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: selectedRole })
      });
      const data = decrypt(await response.json());
      if (data.msg) {
        setCurrentRole(selectedRole);
        alert(data.msg);
      } else alert(JSON.stringify(data.error));
    } catch (error) {
      alert('Failed to update user role: ' + error.message);
    } finally {
      setUpdating(false);
      setRoleDialogOpen(false);
    }
  };

  const roleColors = getRoleColor(currentRole);

  return (
    <>
      <Card sx={{
        borderRadius: '16px',
        border: `1.5px solid ${VV.border}`,
        boxShadow: '0 4px 20px rgba(63,43,150,0.08)',
        transition: 'all 0.25s',
        '&:hover': { boxShadow: '0 8px 32px rgba(63,43,150,0.15)', transform: 'translateY(-2px)' },
      }}>
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Avatar
              src={user.profilePic}
              sx={{
                width: 56,
                height: 56,
                border: `3px solid ${VV.secondary}`,
                fontSize: 22,
                fontWeight: 700,
                bgcolor: `${VV.primary}22`,
                color: VV.primary,
              }}
            >
              {!user.profilePic && (user.username ? user.username[0].toUpperCase() : <PersonIcon />)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: VV.primary, lineHeight: 1.2 }}>
                  {user.username}
                </Typography>
                {blocked && <Chip label="Blocked" size="small" color="error" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmailIcon sx={{ fontSize: 13, color: '#888' }} />
                <Typography variant="caption" sx={{ color: '#666' }}>{user.email || 'N/A'}</Typography>
              </Box>
            </Box>
            <Chip
              icon={<AdminPanelSettingsIcon sx={{ fontSize: 14 }} />}
              label={currentRole.charAt(0).toUpperCase() + currentRole.slice(1)}
              size="small"
              onClick={() => setRoleDialogOpen(true)}
              sx={{ bgcolor: roleColors.bg, color: roleColors.color, fontWeight: 700, borderRadius: '8px', fontSize: '0.7rem', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<EditIcon />}
              onClick={editUser}
              variant="contained"
              fullWidth
              sx={{
                borderRadius: '10px',
                background: `linear-gradient(135deg, ${VV.primary}, ${VV.secondary})`,
                fontWeight: 700,
                fontSize: '0.75rem',
                boxShadow: '0 4px 12px rgba(63,43,150,0.25)',
                '&:hover': { boxShadow: '0 6px 20px rgba(63,43,150,0.35)' },
              }}
            >
              Edit
            </Button>
            <IconButton
              onClick={blockUser}
              sx={{
                bgcolor: blocked ? '#E8F5E9' : '#FFEBEE',
                color: blocked ? '#2E7D32' : '#C62828',
                borderRadius: '10px',
                '&:hover': { bgcolor: blocked ? '#C8E6C9' : '#FFCDD2', transform: 'scale(1.05)' },
                transition: 'all 0.2s',
              }}
            >
              {blocked ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : <BlockIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Box>
        </Box>
      </Card>

      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: VV.primary }}>Update User Role</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} label="Role"
              sx={{ borderRadius: '10px' }}>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="representative">Representative</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setRoleDialogOpen(false)} sx={{ borderRadius: '10px' }}>Cancel</Button>
          <Button onClick={updateUserRole} variant="contained" disabled={updating}
            sx={{ borderRadius: '10px', background: `linear-gradient(135deg, ${VV.primary}, ${VV.secondary})`, fontWeight: 700 }}>
            {updating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SingleUser;
